import type {
    AgentInteractionState,
    InteractionStep,
    OrchestratorAgent,
    OrchestratorState,
    SimpleAgentConfig
} from '@demo-portal/shared';
import React from 'react';

interface OrchestrationDashboardProps {
  orchestratorState: OrchestratorState;
  agentInteractions: Map<string, AgentInteractionState>;
  agents: SimpleAgentConfig;
  orchestrator: OrchestratorAgent;
  currentStep: InteractionStep | null;
  primaryColor?: string;
}

const OrchestrationDashboard: React.FC<OrchestrationDashboardProps> = ({
  orchestratorState,
  agentInteractions,
  agents,
  orchestrator,
  currentStep,
  primaryColor = '#3498db',
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'initializing': return '#f39c12';
      case 'discovering': return '#e67e22';
      case 'ready': return '#27ae60';
      case 'processing': return '#3498db';
      case 'synthesizing': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'initializing': return '⚡';
      case 'discovering': return '🔍';
      case 'ready': return '✅';
      case 'processing': return '⚙️';
      case 'synthesizing': return '🧠';
      default: return '💤';
    }
  };

  const getAgentStatusIcon = (status: string) => {
    switch (status) {
      case 'idle': return '💤';
      case 'called': return '📞';
      case 'processing': return '⚙️';
      case 'responding': return '💬';
      case 'complete': return '✅';
      default: return '❓';
    }
  };

  const activeAgentCount = Array.from(agentInteractions.values())
    .filter(interaction => interaction.status !== 'idle').length;

  const completedAgentCount = Array.from(agentInteractions.values())
    .filter(interaction => interaction.status === 'complete').length;

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      marginBottom: '20px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        paddingBottom: '15px',
        borderBottom: `2px solid ${primaryColor}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '1.5em' }}>📊</span>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>
            Orchestration Dashboard
          </h3>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: `${getStatusColor(orchestratorState.status)}20`,
          padding: '8px 12px',
          borderRadius: '20px',
          border: `1px solid ${getStatusColor(orchestratorState.status)}40`,
        }}>
          <span style={{ fontSize: '1.1em' }}>
            {getStatusIcon(orchestratorState.status)}
          </span>
          <span style={{
            fontWeight: 'bold',
            color: getStatusColor(orchestratorState.status),
            textTransform: 'capitalize',
          }}>
            {orchestratorState.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* System Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px',
        marginBottom: '20px',
      }}>
        {/* Orchestrator Status */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea20, #764ba220)',
          border: '1px solid #667eea40',
          borderRadius: '10px',
          padding: '15px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2em', marginBottom: '8px' }}>
            {orchestrator.avatar}
          </div>
          <div style={{
            fontWeight: 'bold',
            color: '#667eea',
            marginBottom: '4px',
          }}>
            Orchestrator
          </div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>
            {orchestratorState.status === 'ready' ? 'Standing by' : 'Active'}
          </div>
        </div>

        {/* Agent Count */}
        <div style={{
          background: `${primaryColor}20`,
          border: `1px solid ${primaryColor}40`,
          borderRadius: '10px',
          padding: '15px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2em', marginBottom: '8px' }}>
            🤖
          </div>
          <div style={{
            fontWeight: 'bold',
            color: primaryColor,
            marginBottom: '4px',
          }}>
            {agents.length} Agents
          </div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>
            {orchestratorState.discoveredAgents.length} discovered
          </div>
        </div>

        {/* Active Tasks */}
        <div style={{
          background: activeAgentCount > 0 ? '#f39c1220' : '#27ae6020',
          border: `1px solid ${activeAgentCount > 0 ? '#f39c1240' : '#27ae6040'}`,
          borderRadius: '10px',
          padding: '15px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2em', marginBottom: '8px' }}>
            {activeAgentCount > 0 ? '⚙️' : '✅'}
          </div>
          <div style={{
            fontWeight: 'bold',
            color: activeAgentCount > 0 ? '#f39c12' : '#27ae60',
            marginBottom: '4px',
          }}>
            {activeAgentCount} Active
          </div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>
            {completedAgentCount} completed
          </div>
        </div>

        {/* Current Step */}
        <div style={{
          background: currentStep ? '#9b59b620' : '#95a5a620',
          border: `1px solid ${currentStep ? '#9b59b640' : '#95a5a640'}`,
          borderRadius: '10px',
          padding: '15px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2em', marginBottom: '8px' }}>
            {currentStep ? '🔄' : '⏸️'}
          </div>
          <div style={{
            fontWeight: 'bold',
            color: currentStep ? '#9b59b6' : '#95a5a6',
            marginBottom: '4px',
          }}>
            {currentStep ? 'Processing' : 'Idle'}
          </div>
          <div style={{ fontSize: '0.85em', color: '#666' }}>
            {currentStep?.action || 'No active step'}
          </div>
        </div>
      </div>

      {/* Current Step Details */}
      {currentStep && (
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '10px',
          padding: '15px',
          marginBottom: '20px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
          }}>
            <span style={{ fontSize: '1.2em' }}>🎯</span>
            <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Current Step: {currentStep.stepId}
            </span>
          </div>
          <div style={{
            fontSize: '0.9em',
            color: '#495057',
            lineHeight: '1.4',
            marginBottom: '8px',
          }}>
            <strong>Agent:</strong> {currentStep.agent} | 
            <strong> Action:</strong> {currentStep.action} | 
            <strong> Duration:</strong> {currentStep.duration}ms
          </div>
          <div style={{
            fontSize: '0.85em',
            color: '#6c757d',
            fontStyle: 'italic',
          }}>
            {currentStep.description}
          </div>
        </div>
      )}

      {/* Agent Status Grid */}
      {agentInteractions.size > 0 && (
        <div>
          <h4 style={{
            color: '#2c3e50',
            marginBottom: '15px',
            fontSize: '1.1em',
          }}>
            Agent Status Overview
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '10px',
          }}>
            {Array.from(agentInteractions.entries()).map(([agentId, interaction]) => {
              const agent = agents.find(a => a.id === agentId);
              if (!agent) return null;

              return (
                <div
                  key={agentId}
                  style={{
                    background: interaction.status === 'idle' ? '#f8f9fa' : '#e3f2fd',
                    border: `1px solid ${interaction.status === 'idle' ? '#dee2e6' : primaryColor}`,
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <span style={{ fontSize: '1.2em' }}>
                    {agent.avatar}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.9em',
                      color: '#2c3e50',
                      marginBottom: '2px',
                    }}>
                      {agent.name.split(' ')[0]}
                    </div>
                    <div style={{
                      fontSize: '0.8em',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                      <span>{getAgentStatusIcon(interaction.status)}</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {interaction.status}
                      </span>
                    </div>
                  </div>
                  {interaction.progress > 0 && (
                    <div style={{
                      width: '40px',
                      height: '4px',
                      background: '#dee2e6',
                      borderRadius: '2px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${interaction.progress}%`,
                        height: '100%',
                        background: primaryColor,
                        borderRadius: '2px',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Processing Queue */}
      {orchestratorState.processingQueue.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{
            color: '#2c3e50',
            marginBottom: '10px',
            fontSize: '1.1em',
          }}>
            Processing Queue ({orchestratorState.processingQueue.length} steps)
          </h4>
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '10px',
            fontSize: '0.85em',
          }}>
            {orchestratorState.processingQueue.slice(0, 3).map((step, index) => (
              <div key={step.stepId} style={{ marginBottom: '4px' }}>
                {index + 1}. {step.description}
              </div>
            ))}
            {orchestratorState.processingQueue.length > 3 && (
              <div style={{ color: '#856404', fontStyle: 'italic' }}>
                ... and {orchestratorState.processingQueue.length - 3} more steps
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrchestrationDashboard;